import _extends from '@babel/runtime/helpers/extends';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { useQuery } from '@apollo/client';
import { Overlay, Card, Grid, Spinner } from '@enterprise-ui/canvas-ui-react';
import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import CalendarDates from './components/CalendarDates.js';
import InputField from './components/InputField.js';
import NavHeader from './components/NavHeader.js';
import { ACCOUNTING_CALENDAR_QUERY } from './utils/graphqlQuery.js';
import { useKeyPress, formatValue, toActiveMonth, getData, toMonthArray } from './utils/utility.js';

var _excluded = ["autoOk", "className", "data-testid", "disableDates", "disabled", "error", "errorText", "hintText", "highlight", "id", "label", "location", "onUpdate", "placeholder", "required", "value"];
var RetailCalendar = function RetailCalendar(_ref) {
  var autoOk = _ref.autoOk;
    _ref.className;
    var dataTestId = _ref['data-testid'],
    disableDates = _ref.disableDates,
    disabled = _ref.disabled,
    error = _ref.error,
    errorText = _ref.errorText,
    hintText = _ref.hintText,
    highlight = _ref.highlight,
    id = _ref.id,
    label = _ref.label;
    _ref.location;
    var onUpdate = _ref.onUpdate,
    placeholder = _ref.placeholder,
    required = _ref.required,
    value = _ref.value,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    activeMonth = _useState2[0],
    setActiveMonth = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    monthArr = _useState4[0],
    setMonthArr = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedDate = _useState6[0],
    setSelectedDate = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isCalendarVisible = _useState8[0],
    setIsCalendarVisible = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isLoading = _useState10[0],
    setIsLoading = _useState10[1];
  var _useState11 = useState(true),
    _useState12 = _slicedToArray(_useState11, 2),
    inProgress = _useState12[0],
    setInProgress = _useState12[1];
  var _useState13 = useState({}),
    _useState14 = _slicedToArray(_useState13, 2);
    _useState14[0];
    _useState14[1];
  var escPress = useKeyPress(27);
  var _useQuery = useQuery(ACCOUNTING_CALENDAR_QUERY);
    _useQuery.data;
    var refetch = _useQuery.refetch;
  var fetchData = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
      var formattedDate, localStorageData, dateDataFromLocalStorage, fiscalMonthDateData, sDate, eDate, _singleDateData, centeredYear, results, currentTime;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              formattedDate = moment(value).format('YYYYMMDD');
              localStorageData = getData();
              dateDataFromLocalStorage = localStorageData.filter(function (obj) {
                return obj.calendar_date === formattedDate;
              }); // if it IS in local storage, use that
              if (!(dateDataFromLocalStorage.length !== 0)) {
                _context.next = 8;
                break;
              }
              // return only the needed fiscal month
              fiscalMonthDateData = localStorageData.filter(function (obj) {
                return obj.fiscal_month === dateDataFromLocalStorage[0].fiscal_month && obj.fiscal_year === dateDataFromLocalStorage[0].fiscal_year;
              });
              return _context.abrupt("return", fiscalMonthDateData);
            case 8:
              sDate = null;
              eDate = null; // get the passed in date
              {
                _context.next = 12;
                break;
              }
            case 12:
              _context.next = 14;
              return refetch({
                eDate: formattedDate,
                sDate: formattedDate
              }).then(function (resp) {
                return resp.data;
              });
            case 14:
              _singleDateData = _context.sent;
              // get one year's worth of data from january-december
              centeredYear = _singleDateData === null || _singleDateData === void 0 ? void 0 : _singleDateData.getDate.calendar_data.results[0].calendar_year; // start date and end date are on the 28th in the past and the 1st in the future
              // this is because when a month is requested, we check for the 15th
              // of the month and if it does not exist in local storage, we call for it
              // because of this we eliminate the risk of showing incomplete months
              sDate = "".concat(Number(centeredYear - 1), "1228");
              eDate = "".concat(Number(centeredYear) + 1, "0101");
              // call for the full year
              results = false;
              currentTime = Date.now();
              return _context.abrupt("return", results = refetch({
                eDate: eDate,
                sDate: sDate
              }).then(function (resp) {
                // save results data with an created time in local storage for future use
                results = {
                  createdTime: currentTime,
                  value: resp.data.getDate.calendar_data.results
                };

                // save year in local storage
                localStorage.setItem('canvas_calendar_data', JSON.stringify(results));
                // get the passed in date data from the returned api response data

                var dateDataFromApiResults = results.value.filter(function (obj) {
                  return obj.calendar_date === formattedDate;
                });

                // use the passed in date to return only the needed fiscal month
                var fiscalMonthDateData = results.value.filter(function (obj) {
                  var _dateDataFromApiResul, _dateDataFromApiResul2;
                  return (obj === null || obj === void 0 ? void 0 : obj.fiscal_month) === ((_dateDataFromApiResul = dateDataFromApiResults[0]) === null || _dateDataFromApiResul === void 0 ? void 0 : _dateDataFromApiResul.fiscal_month) && (obj === null || obj === void 0 ? void 0 : obj.fiscal_year) === ((_dateDataFromApiResul2 = dateDataFromApiResults[0]) === null || _dateDataFromApiResul2 === void 0 ? void 0 : _dateDataFromApiResul2.fiscal_year);
                });
                return fiscalMonthDateData;
              }));
            case 21:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return function fetchData(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  // formats a passed in external value on component load if needed and sets the selectedDate to that value
  useEffect(function () {
    // only run if there is a passed value and if the passed value does not equal the current value
    if (value && value !== selectedDate) {
      if (value.format) {
        // if the passed value is already formatted, just set that to the selected date
        setSelectedDate(value);
      } else {
        setIsLoading(true);
        // otherwise run it through our formatter
        formatValue(value, fetchData).then(function (resp) {
          setSelectedDate(resp);
          setIsLoading(false);
        })["catch"](function (err) {
          console.error(err);
          setIsLoading(false);
        });
      }
    } else if (!value || value === '') {
      setSelectedDate(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // calls onUpdate when selected date is changed, prevent initial load null call
  useEffect(function () {
    // prevent from calling onUpdate if the value has not changed
    if (value !== selectedDate && onUpdate && selectedDate !== null && selectedDate !== undefined) {
      onUpdate(id, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // call API for calendar data
  var getCalendarData = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var month, year, formattedDate;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              month = activeMonth ? new Date(activeMonth).getMonth() : new Date().getMonth();
              year = activeMonth ? new Date(activeMonth).getFullYear() : new Date().getFullYear(); // JS native months start with 0 *rolls-eyes*
              formattedDate = moment("".concat(month + 1, "/15/").concat(year)).format('YYYYMMDD');
              setInProgress(true);
              _context2.next = 6;
              return fetchData(formattedDate).then(function (resp) {
                setMonthArr(toMonthArray(resp, disableDates));
                setInProgress(false);
              });
            case 6:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));
    return function getCalendarData() {
      return _ref3.apply(this, arguments);
    };
  }();

  // get month of selected date, so we can open calendar with that month showing
  useEffect(function () {
    var dateToExtractMonthFrom = null;
    if (selectedDate) {
      // if there's a current value, set the current month to corespond to that date
      dateToExtractMonthFrom = selectedDate.value;
    } else {
      // if not, set the current month to this month
      dateToExtractMonthFrom = new Date();
      dateToExtractMonthFrom = dateToExtractMonthFrom.toLocaleDateString('en-US');
    }
    setActiveMonth(toActiveMonth(dateToExtractMonthFrom, 'retail'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarVisible, selectedDate]);

  // call to API to get retail calendar data
  var fetchRetailDates = useCallback(function () {
    if (!activeMonth) {
      return false;
    }
    getCalendarData();
  });

  // get inital data if calendar is visible
  useEffect(function () {
    if (isCalendarVisible) {
      fetchRetailDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarVisible, activeMonth]);

  // close on esc
  useEffect(function () {
    escPress && setIsCalendarVisible(false);
  }, [escPress]);

  // change function triggered by the users selecting a previous or next month
  var selectMonth = useCallback(function (type) {
    var newDate = toActiveMonth(activeMonth ? activeMonth : selectedDate, type);
    setActiveMonth(newDate);
  }, [activeMonth, selectedDate]);

  // if no date is passed, clear the date, otherwise set it to the new date
  var handleSetDate = function handleSetDate(date) {
    if (!date) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    isVisible: isCalendarVisible,
    onClick: function onClick() {
      return setIsCalendarVisible(false);
    }
  }), /*#__PURE__*/React.createElement(InputField, _extends({}, otherProps, {
    required: required,
    "data-testid": dataTestId,
    error: error,
    errorText: errorText,
    hintText: hintText,
    highlight: highlight,
    label: label,
    id: id,
    isLoading: isLoading,
    disabled: disabled,
    disableDates: disableDates,
    placeholder: placeholder,
    selectedDate: selectedDate,
    setIsCalendarVisible: setIsCalendarVisible,
    onClear: setSelectedDate,
    onChange: function onChange() {} // prevents warning, eats onChange function
  })), isCalendarVisible && !disabled && /*#__PURE__*/React.createElement(Card, {
    elevation: 2,
    className: "C-DatePicker__Card --retail"
  }, /*#__PURE__*/React.createElement(NavHeader, {
    activeMonth: activeMonth,
    selectMonth: selectMonth,
    inProgress: inProgress
  }), inProgress ? /*#__PURE__*/React.createElement(Grid.Container, {
    align: "center",
    justify: "center"
  }, /*#__PURE__*/React.createElement(Grid.Item, {
    className: "hc-mt-normal"
  }, /*#__PURE__*/React.createElement(Spinner, null))) : /*#__PURE__*/React.createElement(CalendarDates, {
    autoOk: autoOk,
    monthArr: monthArr,
    disableDates: disableDates,
    selectedDate: selectedDate,
    setDate: handleSetDate,
    setIsCalendarVisible: setIsCalendarVisible,
    inProgress: inProgress
  })));
};
var DatePicker = /*#__PURE__*/React.memo(RetailCalendar);

export { RetailCalendar, DatePicker as default };
